import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ServiceCard from '@/components/base/ServiceCard';
import { services, categories } from '@/mocks/categories';

const tabs = [
  { key: 'all', label: 'categories.all' },
  { key: 'beauty', label: 'categories.beauty' },
  { key: 'hair', label: 'categories.hair' },
  { key: 'nail', label: 'categories.nail' },
  { key: 'body', label: 'categories.body' },
];

export default function Categories() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';

  const filtered = useMemo(() => {
    if (activeTab === 'all') return services;
    return services.filter((s) => s.category === activeTab);
  }, [activeTab]);

  const activeCategory = categories.find((c) => c.id === activeTab);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '服务分类 - 91美',
            description: '91美服务分类，涵盖美容、美发、美甲、美体等全品类美业服务。',
            url: 'https://91mei.com/categories',
            isPartOf: {
              '@type': 'WebSite',
              name: '91美',
              url: 'https://91mei.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://91mei.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '服务分类',
                item: 'https://91mei.com/categories',
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[{ label: t('breadcrumb.categories') }]}
          />

          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-8">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {t('categories.title')}
                </h1>
                <p className="text-sm text-foreground-500">
                  {t('categories.subtitle')}
                </p>
              </div>

              <div className="flex items-center justify-center mb-10">
                <div className="inline-flex bg-background-100 rounded-full p-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setSearchParams({ tab: tab.key })}
                      className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                        activeTab === tab.key
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {t(tab.label)}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab !== 'all' && activeCategory && (
                <div className="mb-8 p-5 rounded-lg bg-accent-100/50 border border-accent-200/50 flex flex-col md:flex-row items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-accent-200 flex items-center justify-center shrink-0">
                    <i className={`${activeCategory.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <div>
                    <h2 className="font-semibold text-foreground-900 mb-1">{activeCategory.name}</h2>
                    <p className="text-sm text-foreground-600">{activeCategory.description}</p>
                  </div>
                </div>
              )}

              {filtered.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
                  <p className="text-foreground-500 text-sm">暂无相关服务</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
                  {filtered.map((svc) => (
                    <ServiceCard
                      key={svc.id}
                      name={svc.name}
                      description={svc.description}
                      price={svc.price}
                      originalPrice={svc.originalPrice}
                      duration={svc.duration}
                      rating={svc.rating}
                      reviewCount={svc.reviewCount}
                      store={svc.store}
                      image={svc.image}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}